import type { UserPreferences } from './types';
import { getDreamBotRuntimeConfig } from './runtimeConfig';

/**
 * Retry configuration for API calls
 */
const RETRY_CONFIG = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
};

/**
 * Sleep utility for retry delays
 */
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate exponential backoff delay
 */
const getRetryDelay = (attempt: number): number => {
  const delay = RETRY_CONFIG.initialDelay * Math.pow(RETRY_CONFIG.backoffMultiplier, attempt);
  return Math.min(delay, RETRY_CONFIG.maxDelay);
};

/**
 * Check if error is retryable
 */
const isRetryableError = (error: any): boolean => {
  // Network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }
  
  // HTTP status codes that are retryable
  if (error.status) {
    const retryableStatuses = [408, 429, 500, 502, 503, 504];
    return retryableStatuses.includes(error.status);
  }
  
  return false;
};

/**
 * Retry wrapper with exponential backoff
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  context: string
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 0; attempt <= RETRY_CONFIG.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;
      
      // Don't retry if it's not a retryable error or if we've exhausted retries
      if (!isRetryableError(error) || attempt === RETRY_CONFIG.maxRetries) {
        throw error;
      }
      
      const delay = getRetryDelay(attempt);
      await sleep(delay);
    }
  }
  
  throw lastError;
}

const buildApiUrl = (path: string) => {
  const { apiBaseUrl } = getDreamBotRuntimeConfig();
  const workerBaseUrl = typeof apiBaseUrl === 'string' && apiBaseUrl.length > 0
    ? apiBaseUrl
    : 'https://dreambot-gateway.dev-c2d.workers.dev';

  // Allow callers to provide a full URL (e.g. WP REST endpoint) instead of a path.
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const base = workerBaseUrl.endsWith('/') ? workerBaseUrl.slice(0, -1) : workerBaseUrl;
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
};

const getWpRecommendationUrl = (): string | null => {
  try {
    const origin = (globalThis as any)?.location?.origin;
    if (typeof origin !== 'string' || origin.length === 0) return null;
    return new URL('/wp-json/dreambot/v1/recommendation', origin).toString();
  } catch {
    return null;
  }
};

const getSessionId = (): string => {
  const key = 'dreambot_session_id';
  let sessionId = sessionStorage.getItem(key);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(key, sessionId);
  }
  return sessionId;
};

export async function sendChatMessage(message: string, history?: { role: 'user' | 'model'; text: string }[]) {
  return retryWithBackoff(async () => {
    const { siteId, embedToken, chatPath } = getDreamBotRuntimeConfig();
    const res = await fetch(buildApiUrl(chatPath || '/chat'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(embedToken ? { 'X-DreamBot-Token': embedToken } : {}),
      },
      body: JSON.stringify({ 
        site_id: siteId || 'mattressunboxedco',
        session_id: getSessionId(),
        message, 
        history 
      }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const error: any = new Error(text || 'Chat request failed');
      error.status = res.status;
      throw error;
    }

    const contentType = res.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Server returned an error page instead of JSON. Check server logs.');
    }

    const data: any = await res.json();
    if (data && typeof data.reply === 'string') {
      return { text: data.reply, structured: data.structured };
    }
    if (data && typeof data.text === 'string') {
      return { text: data.text };
    }
    return { text: '' };
  }, 'Chat message');
}

export async function generateRecommendation(prefs: UserPreferences) {
  return retryWithBackoff(async () => {
    const { recommendationPath, embedToken } = getDreamBotRuntimeConfig();
    const wpUrl = getWpRecommendationUrl();
    const workerUrl = buildApiUrl('/api/recommendation');

    // If recommendationPath is explicitly provided, honor it.
    // Otherwise, default to same-origin WP REST so recommendations are driven by the store's WooCommerce catalog.
    const primaryUrl = recommendationPath
      ? buildApiUrl(recommendationPath)
      : (wpUrl || workerUrl);

    const doFetch = async (url: string) => {
      let res: Response;
      try {
        res = await fetch(url, {
          method: 'POST',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json',
            ...(embedToken ? { 'X-DreamBot-Token': embedToken } : {}),
          },
          body: JSON.stringify({ prefs }),
        });
      } catch (err: any) {
        // Network/CORS errors surface as TypeError without a status code.
        const error: any = new Error(err?.message || 'Recommendation request failed');
        error.status = 0;
        error.cause = err;
        throw error;
      }

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        const error: any = new Error(text || 'Recommendation request failed');
        error.status = res.status;
        throw error;
      }

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.toLowerCase().includes('application/json')) {
        const text = await res.text().catch(() => '');
        const error: any = new Error('Recommendation endpoint returned non-JSON response');
        error.status = 0;
        error.httpStatus = res.status;
        error.contentType = contentType;
        error.bodySnippet = text.substring(0, 500);
        throw error;
      }

      const data: any = await res.json();
      if (data && typeof data.json === 'string') {
        return data as { json: string };
      }
      return { json: JSON.stringify(data ?? {}) };
    };

    const candidates = [primaryUrl, wpUrl, workerUrl].filter((u): u is string => typeof u === 'string' && u.length > 0);
    const seen = new Set<string>();

    let lastErr: any;
    for (const url of candidates) {
      if (seen.has(url)) continue;
      seen.add(url);

      try {
        return await doFetch(url);
      } catch (e: any) {
        lastErr = e;

        // Retry next candidate for:
        // - CORS/network failures (status 0)
        // - route not found / method mismatch
        if (e && (e.status === 0 || e.status === 404 || e.status === 405)) {
          continue;
        }

        throw e;
      }
    }

    throw lastErr;
  }, 'Recommendation');
}

export async function generateRecommendationWithQuery(prefs: UserPreferences, query: string) {
  return retryWithBackoff(async () => {
    const { recommendationPath, embedToken } = getDreamBotRuntimeConfig();
    const wpUrl = getWpRecommendationUrl();
    const workerUrl = buildApiUrl('/api/recommendation');

    const primaryUrl = recommendationPath
      ? buildApiUrl(recommendationPath)
      : (wpUrl || workerUrl);

    const doFetch = async (url: string) => {
      let res: Response;
      try {
        res = await fetch(url, {
          method: 'POST',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json',
            ...(embedToken ? { 'X-DreamBot-Token': embedToken } : {}),
          },
          body: JSON.stringify({ prefs, query }),
        });
      } catch (err: any) {
        const error: any = new Error(err?.message || 'Recommendation request failed');
        error.status = 0;
        error.cause = err;
        throw error;
      }

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        const error: any = new Error(text || 'Recommendation request failed');
        error.status = res.status;
        throw error;
      }

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.toLowerCase().includes('application/json')) {
        const text = await res.text().catch(() => '');
        const error: any = new Error('Recommendation endpoint returned non-JSON response');
        error.status = 0;
        error.httpStatus = res.status;
        error.contentType = contentType;
        error.bodySnippet = text.substring(0, 500);
        throw error;
      }

      const data: any = await res.json();
      if (data && typeof data.json === 'string') {
        return data as { json: string };
      }
      return { json: JSON.stringify(data ?? {}) };
    };

    const candidates = [primaryUrl, wpUrl, workerUrl].filter((u): u is string => typeof u === 'string' && u.length > 0);
    const seen = new Set<string>();

    let lastErr: any;
    for (const url of candidates) {
      if (seen.has(url)) continue;
      seen.add(url);

      try {
        return await doFetch(url);
      } catch (e: any) {
        lastErr = e;
        if (e && (e.status === 0 || e.status === 404 || e.status === 405)) {
          continue;
        }
        throw e;
      }
    }

    throw lastErr;
  }, 'Recommendation');
}
